import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useApi } from '../../../hooks/useApi';

const headerColor = '#111001'; // light yellow
const issueColor = '#ffcdd2'; // light red/pink
const badColor = '#f44336'; // red

const scoreColor = (score) => {
  if (score === 0) return badColor;
  if (score > 0 && score <= 5) return '#ff9800'; // orange
  if (score > 5 && score <= 7) return '#ffeb3b'; // yellow
  return '#4e9450'; // light green
};

function WorkerStats({ stats }) {
  if (!stats?.workerStats) return null;

  return (
    <TableContainer sx={{ mb: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ bgcolor: headerColor }}>Num CPUs</TableCell>
            <TableCell>{stats.workerStats.numCPUs}</TableCell>
            <TableCell sx={{ bgcolor: headerColor }}>Node Version</TableCell>
            <TableCell colSpan={3}>{stats.workerStats.nodeVersion}</TableCell>
          </TableRow>
        </TableHead>
        <TableHead>
          <TableRow sx={{ bgcolor: headerColor }}>
            <TableCell>Rooms</TableCell>
            <TableCell>RSS</TableCell>
            <TableCell>Heap Total</TableCell>
            <TableCell>Heap Used</TableCell>
            <TableCell>External</TableCell>
            <TableCell>Array Buffers</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>{stats.workerStats.numRooms}</TableCell>
            <TableCell>{stats.workerStats.memoryUsage?.rss}</TableCell>
            <TableCell>{stats.workerStats.memoryUsage?.heapTotal}</TableCell>
            <TableCell>{stats.workerStats.memoryUsage?.heapUsed}</TableCell>
            <TableCell>{stats.workerStats.memoryUsage?.external}</TableCell>
            <TableCell>{stats.workerStats.memoryUsage?.arrayBuffers}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function RoomStats({ stats }) {
  if (!stats?.roomStats) return null;

  return (
    <TableContainer>
      <Table size="small">
        {stats.roomStats.map((room, roomIndex) => (
          <React.Fragment key={roomIndex}>
            {/* Room Header */}
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: headerColor }}>Room</TableCell>
                <TableCell colSpan={2}>{room.roomName}</TableCell>
                <TableCell sx={{ bgcolor: headerColor }}>Owner</TableCell>
                <TableCell colSpan={2}>{room.owner}</TableCell>
                <TableCell sx={{ bgcolor: headerColor }}>Room IP</TableCell>
                <TableCell colSpan={4}>{room.roomIP}</TableCell>
              </TableRow>
            </TableHead>

            {/* Transport Stats */}
            <TableHead>
              <TableRow>
                <TableCell colSpan={11}><strong>Transport Stats</strong></TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: headerColor }}>
                <TableCell>Owner</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell>Bytes Recv</TableCell>
                <TableCell>Recv Bitrate</TableCell>
                <TableCell>Bytes Sent</TableCell>
                <TableCell>Send Bitrate</TableCell>
                <TableCell>Remote IP</TableCell>
                <TableCell>Remote Port</TableCell>
                <TableCell colSpan={2}>Protocol</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {room.transportStats?.map((transport, tIndex) =>
                transport.stats?.map((stat, sIndex) => (
                  <TableRow key={`t-${tIndex}-${sIndex}`}>
                    <TableCell>{transport.owner}</TableCell>
                    <TableCell>{stat.dtlsState}</TableCell>
                    <TableCell>{stat.availableOutgoingBitrate ? 'Outbound' : 'Inbound'}</TableCell>
                    <TableCell>{stat.bytesReceived}</TableCell>
                    <TableCell>{stat.recvBitrate}</TableCell>
                    <TableCell>{stat.bytesSent}</TableCell>
                    <TableCell>{stat.sendBitrate}</TableCell>
                    <TableCell>{stat.iceSelectedTuple?.remoteIp}</TableCell>
                    <TableCell>{stat.iceSelectedTuple?.remotePort}</TableCell>
                    <TableCell colSpan={2}>{stat.iceSelectedTuple?.protocol}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>

            {/* Producer Stats */}
            <TableHead>
              <TableRow>
                <TableCell colSpan={11}><strong>Producer Stats</strong></TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: headerColor }}>
                <TableCell>Owner</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Kind</TableCell>
                <TableCell>Mimetype</TableCell>
                <TableCell>Packets Lost</TableCell>
                <TableCell>Retransmit</TableCell>
                <TableCell>RTT</TableCell>
                <TableCell>Jitter</TableCell>
                <TableCell>Packets</TableCell>
                <TableCell>Score</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {room.producerStats?.map((producer, pIndex) =>
                producer.stats?.map((stat, sIndex) => (
                  <TableRow key={`p-${pIndex}-${sIndex}`}>
                    <TableCell>{producer.owner}</TableCell>
                    <TableCell>{producer.status}</TableCell>
                    <TableCell>{stat.type}</TableCell>
                    <TableCell>{stat.kind}</TableCell>
                    <TableCell>{stat.mimeType}</TableCell>
                    <TableCell sx={{ bgcolor: stat.packetsLost > 100 ? issueColor : undefined }}>
                      {stat.packetsLost}
                    </TableCell>
                    <TableCell sx={{ bgcolor: stat.packetsRetransmitted > 1000 ? issueColor : undefined }}>
                      {stat.packetsRetransmitted}
                    </TableCell>
                    <TableCell sx={{ bgcolor: stat.roundTripTime > 100 ? issueColor : undefined }}>
                      {parseFloat((stat.roundTripTime || 0).toFixed(2))}
                    </TableCell>
                    <TableCell sx={{ bgcolor: stat.jitter > 30 ? (stat.jitter > 100 ? badColor : issueColor) : undefined }}>
                      {stat.jitter}
                    </TableCell>
                    <TableCell>{stat.packetCount}</TableCell>
                    <TableCell sx={{ bgcolor: scoreColor(stat.score) }}>{stat.score}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>

            {/* Consumer Stats */}
            <TableHead>
              <TableRow>
                <TableCell colSpan={12}><strong>Consumer Stats</strong></TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: headerColor }}>
                <TableCell>Owner</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Producer</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Kind</TableCell>
                <TableCell>Mimetype</TableCell>
                <TableCell>Lost</TableCell>
                <TableCell>Retrans</TableCell>
                <TableCell>RTT</TableCell>
                <TableCell>Jitter</TableCell>
                <TableCell>Packets</TableCell>
                <TableCell>Score</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {room.consumerStats?.map((consumer, cIndex) =>
                consumer.stats?.map((stat, sIndex) => (
                  <TableRow key={`c-${cIndex}-${sIndex}`}>
                    <TableCell>{consumer.owner}</TableCell>
                    <TableCell>{consumer.status}</TableCell>
                    <TableCell>{consumer.producerUserId}</TableCell>
                    <TableCell>{stat.type}</TableCell>
                    <TableCell>{stat.kind}</TableCell>
                    <TableCell>{stat.mimeType}</TableCell>
                    <TableCell sx={{ bgcolor: stat.packetsLost > 100 ? issueColor : undefined }}>
                      {stat.packetsLost}
                    </TableCell>
                    <TableCell sx={{ bgcolor: stat.packetsRetransmitted > 1000 ? issueColor : undefined }}>
                      {stat.packetsRetransmitted}
                    </TableCell>
                    <TableCell sx={{ bgcolor: stat.roundTripTime > 100 ? issueColor : undefined }}>
                      {parseFloat((stat.roundTripTime || 0).toFixed(2))}
                    </TableCell>
                    <TableCell sx={{ bgcolor: stat.jitter > 30 ? (stat.jitter > 100 ? badColor : issueColor) : undefined }}>
                      {stat.jitter}
                    </TableCell>
                    <TableCell>{stat.packetCount}</TableCell>
                    <TableCell sx={{ bgcolor: scoreColor(stat.score) }}>{stat.score}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </React.Fragment>
        ))}
      </Table>
    </TableContainer>
  );
}

function Codecs({ stats }) {
  if (!stats?.roomStats) return null;

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: headerColor }}>
            <TableCell>Kind</TableCell>
            <TableCell>Mimetype</TableCell>
            <TableCell>Clock Rate</TableCell>
            <TableCell>Parameters</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {stats.roomStats.map((room) =>
            room.rtpCapabilities?.codecs?.map((codec, index) => (
              <TableRow key={index}>
                <TableCell>{codec.kind}</TableCell>
                <TableCell>{codec.mimeType}</TableCell>
                <TableCell>{codec.clockRate}</TableCell>
                <TableCell>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {Object.entries(codec.parameters || {}).map(([key, value]) => (
                      <li key={key}>{`${key}: ${value}`}</li>
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function RoomStatsDialog({ open, onClose, roomName }) {
  const api = useApi();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);

  const fetchStats = async () => {
    if (!roomName) return;

    try {
      setLoading(true);
      const data = await api.post('/api/status/room-stats', { roomName });
      setStats(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching room stats:', err);
      setError('Failed to fetch room stats');
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchStats();
      const interval = setInterval(fetchStats, 5000);
      return () => clearInterval(interval);
    }
  }, [open, roomName]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle>Active Room Stats: {roomName}</DialogTitle>
      <DialogContent>
        {loading && !stats ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : stats ? (
          <Box>
            <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>Worker Stats</Typography>
            <WorkerStats stats={stats} />

            <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)} sx={{ mb: 2 }}>
              <Tab label="Room Stats" />
              <Tab label="Codecs" />
            </Tabs>

            {selectedTab === 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>Room Stats</Typography>
                <RoomStats stats={stats} />
              </Box>
            )}

            {selectedTab === 1 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>Codecs</Typography>
                <Codecs stats={stats} />
              </Box>
            )}
          </Box>
        ) : (
          <Typography sx={{
            color: "text.secondary"
          }}>No room information available.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default RoomStatsDialog;
